import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  TrendingUp,
  TrendingDown,
  Wallet,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  FileSpreadsheet,
  Eye,
  PiggyBank,
  Landmark
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from "recharts";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

const BANK_COLORS = [
  "#dc2626", // red
  "#2563eb", // blue
  "#16a34a", // green
  "#9333ea", // purple
  "#ea580c", // orange
  "#0891b2", // cyan
  "#4f46e5", // indigo
  "#be185d", // pink
];

const ContiBancari = () => {
  const { getAuthHeaders } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [detailsAccount, setDetailsAccount] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    bank_name: "",
    account_number: "",
    initial_balance: "",
    color: "#2563eb",
    notes: ""
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bank-accounts`, getAuthHeaders());
      setAccounts(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento conti bancari");
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountDetails = async (accountId) => {
    try {
      const response = await axios.get(
        `${API}/bank-accounts/${accountId}/summary`,
        getAuthHeaders()
      );
      setAccountDetails(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dettagli");
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.bank_name) {
      toast.error("Nome e banca sono obbligatori");
      return;
    }

    try {
      const data = {
        ...formData,
        initial_balance: parseFloat(formData.initial_balance) || 0
      };

      if (editingAccount) {
        await axios.put(
          `${API}/bank-accounts/${editingAccount.id}`,
          data,
          getAuthHeaders()
        );
        toast.success("Conto aggiornato");
      } else {
        await axios.post(`${API}/bank-accounts`, data, getAuthHeaders());
        toast.success("Conto creato");
      }

      setDialogOpen(false);
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo conto?")) return;

    try {
      await axios.delete(`${API}/bank-accounts/${accountId}`, getAuthHeaders());
      toast.success("Conto eliminato");
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const resetForm = () => {
    setEditingAccount(null);
    setFormData({
      name: "",
      bank_name: "",
      account_number: "",
      initial_balance: "",
      color: BANK_COLORS[accounts.length % BANK_COLORS.length],
      notes: ""
    });
  };

  const openEditDialog = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      bank_name: account.bank_name,
      account_number: account.account_number || "",
      initial_balance: account.initial_balance || "",
      color: account.color || "#2563eb",
      notes: account.notes || ""
    });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openDetails = (account) => {
    setDetailsAccount(account);
    fetchAccountDetails(account.id);
  };

  const totalBalance = accounts.reduce((sum, acc) => sum + (acc.current_balance || 0), 0);
  const totalIncome = accounts.reduce((sum, acc) => sum + (acc.income || 0), 0);
  const totalExpense = accounts.reduce((sum, acc) => sum + (acc.expense || 0), 0);

  const pieData = accounts.map(acc => ({
    name: acc.name,
    value: Math.abs(acc.current_balance || 0),
    color: acc.color
  })).filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="conti-bancari-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1D1D1F]">Conti Bancari</h1>
          <p className="text-[#86868B] mt-1">
            Gestisci i tuoi conti bancari e monitora i saldi
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchAccounts}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </Button>
          <Button
            onClick={openNewDialog}
            className="bg-[#007AFF] hover:bg-[#0056b3] gap-2"
            data-testid="add-bank-account-btn"
          >
            <Plus className="w-4 h-4" />
            Nuovo Conto
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#86868B] text-sm">Saldo Totale Banche</p>
                <p className={`text-2xl font-bold ${totalBalance >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                  {formatCurrency(totalBalance)}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#007AFF]/10 rounded-xl flex items-center justify-center">
                <Landmark className="w-6 h-6 text-[#007AFF]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#86868B] text-sm">Conti Attivi</p>
                <p className="text-2xl font-bold text-[#1D1D1F]">{accounts.length}</p>
              </div>
              <div className="w-12 h-12 bg-[#AF52DE]/10 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-[#AF52DE]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#86868B] text-sm">Totale Entrate</p>
                <p className="text-2xl font-bold text-[#34C759]">{formatCurrency(totalIncome)}</p>
              </div>
              <div className="w-12 h-12 bg-[#34C759]/10 rounded-xl flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-[#34C759]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#86868B] text-sm">Totale Uscite</p>
                <p className="text-2xl font-bold text-[#FF3B30]">{formatCurrency(totalExpense)}</p>
              </div>
              <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center">
                <ArrowDownRight className="w-6 h-6 text-[#FF3B30]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Accounts List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Accounts List */}
        <div className="lg:col-span-2">
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                I Tuoi Conti
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-48">
                  <RefreshCw className="w-8 h-8 animate-spin text-[#86868B]" />
                </div>
              ) : accounts.length === 0 ? (
                <div className="text-center py-12">
                  <PiggyBank className="w-16 h-16 mx-auto text-[#86868B] mb-4" />
                  <p className="text-[#86868B]">Nessun conto bancario configurato</p>
                  <Button
                    onClick={openNewDialog}
                    className="mt-4 bg-[#007AFF] hover:bg-[#0056b3]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Aggiungi il primo conto
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {accounts.map((account) => (
                    <div
                      key={account.id}
                      className="p-4 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors"
                      style={{ borderLeftWidth: '4px', borderLeftColor: account.color }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${account.color}20` }}
                          >
                            <Building2 className="w-6 h-6" style={{ color: account.color }} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#1D1D1F]">{account.name}</h3>
                            <p className="text-sm text-[#86868B]">{account.bank_name}</p>
                            {account.account_number && (
                              <p className="text-xs text-[#86868B] font-mono">
                                {account.account_number.replace(/(.{4})/g, '$1 ').trim()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-bold ${account.current_balance >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                            {formatCurrency(account.current_balance)}
                          </p>
                          <p className="text-xs text-[#86868B]">
                            {account.movements_count || 0} movimenti
                          </p>
                        </div>
                      </div>
                      
                      {/* Mini stats */}
                      <div className="mt-4 grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <ArrowUpRight className="w-4 h-4 text-[#34C759]" />
                          <span className="text-sm text-[#86868B]">Entrate:</span>
                          <span className="text-sm font-medium text-[#34C759]">
                            {formatCurrency(account.income || 0)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ArrowDownRight className="w-4 h-4 text-[#FF3B30]" />
                          <span className="text-sm text-[#86868B]">Uscite:</span>
                          <span className="text-sm font-medium text-[#FF3B30]">
                            {formatCurrency(account.expense || 0)}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="mt-4 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDetails(account)}
                          className="gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Dettagli
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(account)}
                          className="gap-1"
                        >
                          <Pencil className="w-4 h-4" />
                          Modifica
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(account.id)}
                          className="gap-1 text-[#FF3B30] hover:text-[#FF3B30] hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Elimina
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pie Chart */}
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
              Distribuzione Saldi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-[#86868B]">
                Nessun dato disponibile
              </div>
            )}
            
            {/* Legend */}
            <div className="mt-4 space-y-2">
              {accounts.map((account) => (
                <div key={account.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: account.color }}
                    />
                    <span className="text-[#86868B]">{account.name}</span>
                  </div>
                  <span className="font-medium text-[#1D1D1F]">
                    {formatCurrency(account.current_balance)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingAccount ? "Modifica Conto Bancario" : "Nuovo Conto Bancario"}
            </DialogTitle>
            <DialogDescription>
              {editingAccount 
                ? "Modifica i dettagli del conto bancario" 
                : "Aggiungi un nuovo conto bancario per tracciare i movimenti"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nome Conto *</Label>
              <Input
                id="name"
                placeholder="es. Conto Principale"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="bank_name">Nome Banca *</Label>
              <Input
                id="bank_name"
                placeholder="es. UniCredit, Intesa Sanpaolo..."
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="account_number">IBAN / Numero Conto</Label>
              <Input
                id="account_number"
                placeholder="IT60X0542811101000000123456"
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="initial_balance">Saldo Iniziale</Label>
              <Input
                id="initial_balance"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.initial_balance}
                onChange={(e) => setFormData({ ...formData, initial_balance: e.target.value })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label>Colore Identificativo</Label>
              <div className="flex gap-2 flex-wrap">
                {BANK_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      formData.color === color ? 'scale-110 border-gray-800' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                  />
                ))}
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="notes">Note</Label>
              <Input
                id="notes"
                placeholder="Note aggiuntive..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSubmit} className="bg-[#007AFF] hover:bg-[#0056b3]">
              {editingAccount ? "Salva Modifiche" : "Crea Conto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={!!detailsAccount} onOpenChange={() => { setDetailsAccount(null); setAccountDetails(null); }}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${detailsAccount?.color}20` }}
              >
                <Building2 className="w-4 h-4" style={{ color: detailsAccount?.color }} />
              </div>
              {detailsAccount?.name}
            </DialogTitle>
            <DialogDescription>
              {detailsAccount?.bank_name} {detailsAccount?.account_number && `- ${detailsAccount.account_number}`}
            </DialogDescription>
          </DialogHeader>
          
          {accountDetails ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-gray-50">
                  <p className="text-sm text-[#86868B]">Saldo Attuale</p>
                  <p className={`text-xl font-bold ${accountDetails.current_balance >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                    {formatCurrency(accountDetails.current_balance)}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-gray-50">
                  <p className="text-sm text-[#86868B]">Totale Entrate</p>
                  <p className="text-xl font-bold text-[#34C759]">
                    {formatCurrency(accountDetails.total_income)}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-gray-50">
                  <p className="text-sm text-[#86868B]">Totale Uscite</p>
                  <p className="text-xl font-bold text-[#FF3B30]">
                    {formatCurrency(accountDetails.total_expense)}
                  </p>
                </div>
              </div>

              {/* Monthly Breakdown */}
              {accountDetails.by_month?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-[#1D1D1F] mb-3">Andamento Mensile</h4>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={accountDetails.by_month.slice(0, 6)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Bar dataKey="income" name="Entrate" fill="#34C759" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="expense" name="Uscite" fill="#FF3B30" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Recent Movements */}
              {accountDetails.recent_movements?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-[#1D1D1F] mb-3">Ultimi Movimenti</h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {accountDetails.recent_movements.map((mov, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          {mov.amount > 0 ? (
                            <ArrowUpRight className="w-4 h-4 text-[#34C759]" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4 text-[#FF3B30]" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-[#1D1D1F] line-clamp-1">
                              {mov.description}
                            </p>
                            <p className="text-xs text-[#86868B]">{mov.date}</p>
                          </div>
                        </div>
                        <span className={`font-medium ${mov.amount > 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                          {formatCurrency(mov.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#86868B]" />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetailsAccount(null); setAccountDetails(null); }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContiBancari;
